import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Floor, Room, Bed, Hostel, Tenant } from '@/api/types';
import {
  ChevronDown, ChevronRight, BedDouble, Users, Layers, User, Wrench,
  Building2, X, LogOut, CreditCard, Camera, Upload, Ban, AlertCircle, Plus, Trash2, ArrowLeftRight, Move,
} from 'lucide-react';
import { SwapBedPanel } from '@/features/beds/SwapBedPanel';
import { TransferBedPanel } from '@/features/beds/TransferBedPanel';
import { SignOnBehalfFields, EMPTY_SIGN_ON_BEHALF, type SignOnBehalfValue } from '@/components/SignatureCapture';
import { Select } from '@/components/ui/Select';
import { sanitizePhoneInput } from '@/lib/utils';
import { useTenantPhoneLookup } from '@/features/tenants/useTenantPhoneLookup';
import { TenantPhoneMatchDialog } from '@/features/tenants/TenantPhoneMatchDialog';

interface HostelStats {
  activeTenants: number;
  reservedBeds: number;
  vacantBeds: number;
  checkedOutTenants: number;
  cancelledTenants: number;
  blockedBeds: number;
}

const BED_STATUS_CLASS = {
  VACANT:      'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
  OCCUPIED:    'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  RESERVED:    'bg-amber-100 text-amber-800 border-amber-400 hover:bg-amber-200 ring-1 ring-amber-300',
  MAINTENANCE: 'bg-gray-100 text-gray-500 border-gray-200 cursor-default',
} as const;

// ── Bed card ───────────────────────────────────────────────
function BedCard({ bed, onClick }: { bed: Bed; onClick: () => void }) {
  const { t } = useTranslation();
  const BED_STATUS_LABEL: Record<string, string> = {
    VACANT: t('supervisor.bedFree', 'Free'),
    OCCUPIED: t('room.occupied'),
    RESERVED: t('common.reserved'),
    MAINTENANCE: t('supervisor.bedMaintenanceShort', 'Maint.'),
  };
  const cls = BED_STATUS_CLASS[bed.status] ?? BED_STATUS_CLASS.VACANT;
  const label = BED_STATUS_LABEL[bed.status] ?? bed.status;
  return (
    <button
      onClick={onClick}
      disabled={bed.status === 'MAINTENANCE'}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition ${cls}`}
    >
      {bed.bedLabel}
      <span className="font-normal opacity-70">{label}</span>
    </button>
  );
}

// ── Photo capture ──────────────────────────────────────────
function PhotoCapture({ value, onChange, mode }: {
  value: string; onChange: (b64: string) => void; mode: 'square' | 'landscape';
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showCam, setShowCam] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const resize = (file: File): Promise<string> =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const c = document.createElement('canvas');
        if (mode === 'square') {
          c.width = 300; c.height = 300;
          const ctx = c.getContext('2d')!;
          const s = Math.min(img.width, img.height);
          ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 300, 300);
        } else {
          c.width = 800; c.height = 600;
          const ctx = c.getContext('2d')!;
          const scale = Math.min(800 / img.width, 600 / img.height);
          const sw = img.width * scale; const sh = img.height * scale;
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
          ctx.drawImage(img, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
        }
        URL.revokeObjectURL(url);
        resolve(c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9));
      };
      img.src = url;
    });

  useEffect(() => {
    if (stream && videoRef.current && showCam) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, showCam]);

  const openCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: mode === 'square'
          ? { facingMode: 'user', width: 640, height: 640 }
          : { facingMode: 'environment', width: 1280, height: 720 },
      });
      setStream(s);
      setShowCam(true);
    } catch { fileRef.current?.click(); }
  };

  const capture = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    if (mode === 'square') {
      c.width = 300; c.height = 300;
      const ctx = c.getContext('2d')!;
      const s = Math.min(v.videoWidth, v.videoHeight);
      ctx.drawImage(v, (v.videoWidth - s) / 2, (v.videoHeight - s) / 2, s, s, 0, 0, 300, 300);
    } else {
      c.width = 800; c.height = 600;
      const ctx = c.getContext('2d')!;
      const scale = Math.min(800 / v.videoWidth, 600 / v.videoHeight);
      const sw = v.videoWidth * scale; const sh = v.videoHeight * scale;
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
      ctx.drawImage(v, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
    }
    const b64 = c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9);
    stream?.getTracks().forEach(t => t.stop());
    setStream(null); setShowCam(false);
    onChange(b64);
  };

  return (
    <>
      <div className={`flex flex-col gap-1.5 ${mode === 'square' ? 'items-center' : ''}`}>
        {value ? (
          <div className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <img src={value} alt="Captured" className="w-full h-full object-contain" />
            <button type="button" onClick={() => onChange('')}
              className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">✕</button>
          </div>
        ) : (
          <div className={`rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <Camera size={mode === 'square' ? 20 : 28} />
          </div>
        )}
        <div className="flex gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 text-[11px] border border-gray-300 px-2 py-1 rounded-md hover:bg-gray-50 text-gray-600">
            <Upload size={10} /> {t('supervisor.upload', 'Upload')}
          </button>
          <button type="button" onClick={openCam}
            className="flex items-center gap-1 text-[11px] border border-violet-300 text-violet-700 px-2 py-1 rounded-md hover:bg-violet-50">
            <Camera size={10} /> {t('supervisor.camera', 'Camera')}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={async e => { const f = e.target.files?.[0]; if (f) { onChange(await resize(f)); } e.target.value = ''; }} />
      <canvas ref={canvasRef} className="hidden" />
      {showCam && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-chrome-page rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="font-semibold text-sm">{t('supervisor.capturePhoto', 'Capture Photo')}</p>
              <button type="button" onClick={() => { stream?.getTracks().forEach(t => t.stop()); setStream(null); setShowCam(false); }}
                className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
            </div>
            <div className="p-3 space-y-2">
              <video ref={videoRef} className={`w-full rounded-lg bg-black ${mode === 'square' ? 'aspect-square' : ''}`}
                style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined} muted playsInline />
              <button type="button" onClick={capture}
                className="w-full bg-violet-600 text-white py-2 rounded-xl text-sm font-semibold">
                📷 {t('supervisor.capture', 'Capture')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Checkout form ──────────────────────────────────────────
function CheckoutForm({ onSubmit, isPending, onCancel }: {
  onSubmit: (d: { checkoutDate: string; notes?: string; refundDeposit?: boolean }) => void;
  isPending: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    checkoutDate: new Date().toISOString().split('T')[0],
    notes: '', refundDeposit: false,
  });
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
      <h3 className="font-semibold text-red-900 mb-3">{t('supervisor.confirmCheckout', 'Confirm Check-Out')}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.checkoutDateRequired', 'Check-Out Date *')}</label>
          <input type="date" value={form.checkoutDate}
            onChange={e => setForm(p => ({ ...p, checkoutDate: e.target.value }))}
            className="w-full border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2 self-end pb-1.5">
          <input type="checkbox" id="refund-sup" checked={form.refundDeposit}
            onChange={e => setForm(p => ({ ...p, refundDeposit: e.target.checked }))} className="rounded" />
          <label htmlFor="refund-sup" className="text-sm text-gray-700">{t('supervisor.refundSecurityDeposit', 'Refund security deposit')}</label>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.notes', 'Notes')}</label>
          <textarea rows={2} value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none" />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => onSubmit(form)} disabled={isPending}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? t('supervisor.processing', 'Processing…') : t('supervisor.confirmCheckout', 'Confirm Check-Out')}
        </button>
        <button onClick={onCancel} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

// ── Occupied bed view ──────────────────────────────────────
function OccupiedBedView({ bedId, hostelId, roomNumber, bedLabel, onSuccess }: { bedId: string; hostelId: string; roomNumber: string; bedLabel: string; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const { data: tenant, isLoading } = useQuery<Tenant | null>({
    queryKey: ['bed-tenant', bedId],
    queryFn: () => client.get(`/api/beds/${bedId}/tenant`).then(r => r as unknown as Tenant | null),
  });

  const checkoutMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      client.post(`/api/tenants/${tenant!.id}/checkout`, body),
    onSuccess,
  });

  if (isLoading) return <p className="text-gray-400 py-6 text-center">{t('supervisor.loadingTenantInfo', 'Loading tenant info…')}</p>;
  if (!tenant) return <p className="text-gray-500 py-6 text-center">{t('supervisor.noActiveTenantForBed', 'No active tenant found for this bed.')}</p>;

  return (
    <div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-5">
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.fullName')}</p><p className="font-semibold text-gray-900">{tenant.fullName}</p></div>
        <div><p className="text-xs text-gray-400 mb-0.5">{t('common.phone')}</p><p className="font-semibold">{tenant.phone}</p></div>
        {tenant.email && <div><p className="text-xs text-gray-400 mb-0.5">{t('common.email')}</p><p className="font-medium">{tenant.email}</p></div>}
        {tenant.gender && <div><p className="text-xs text-gray-400 mb-0.5">{t('supervisor.gender', 'Gender')}</p><p className="font-medium">{{ MALE: t('supervisor.male'), FEMALE: t('supervisor.female'), OTHER: t('supervisor.other') }[tenant.gender] ?? tenant.gender}</p></div>}
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.joiningDate')}</p><p className="font-medium">{tenant.joiningDate}</p></div>
        {tenant.expectedCheckoutDate && <div><p className="text-xs text-gray-400 mb-0.5">{t('supervisor.expectedCheckout', 'Expected Checkout')}</p><p className="font-medium">{tenant.expectedCheckoutDate}</p></div>}
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.monthlyRent')}</p><p className="font-semibold text-violet-700">{tenant.monthlyRent != null ? `₹${Number(tenant.monthlyRent).toLocaleString()}/mo` : '—'}</p></div>
        <div><p className="text-xs text-gray-400 mb-0.5">{t('supervisor.securityDeposit', 'Security Deposit')}</p><p className="font-medium">{tenant.securityDeposit != null ? `₹${Number(tenant.securityDeposit).toLocaleString()}` : '—'}</p></div>
        {tenant.notes && <div className="col-span-2"><p className="text-xs text-gray-400 mb-0.5">{t('supervisor.notes', 'Notes')}</p><p className="text-sm text-gray-700">{tenant.notes}</p></div>}
      </div>

      {!showCheckout && !showSwap && !showTransfer ? (
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/supervisor/hostels/$id/tenants/$tenantId"
            params={{ id: hostelId, tenantId: tenant.id }}
            search={{ tab: 'profile' }}
            title={t('owner.viewProfile', 'View Profile')}
            className="flex items-center gap-1.5 text-sm text-violet-600 border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50 font-medium"
          >
            <User size={14} /> {t('tenant.profile')}
          </Link>
          <Link to="/supervisor/hostels/$id/tenants" params={{ id: hostelId }} search={{ status: 'ACTIVE' }}
            className="text-sm text-violet-600 border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50 font-medium">
            {t('supervisor.allTenants', 'All Tenants')}
          </Link>
          <button onClick={() => setShowTransfer(true)}
            className="flex items-center gap-1.5 text-sm text-blue-600 border border-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 font-medium">
            <Move size={14} /> {t('owner.transferRoom', 'Transfer Room')}
          </button>
          <button onClick={() => setShowSwap(true)}
            className="flex items-center gap-1.5 text-sm text-indigo-600 border border-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-50 font-medium">
            <ArrowLeftRight size={14} /> {t('owner.swapBed', 'Swap Bed')}
          </button>
          <button onClick={() => setShowCheckout(true)}
            className="flex items-center gap-1.5 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium">
            <LogOut size={14} /> {t('tenant.checkOut')}
          </button>
        </div>
      ) : showSwap ? (
        <SwapBedPanel
          hostelId={hostelId}
          lockedTenant={{ ...tenant, roomNumber, bedLabel }}
          onClose={() => setShowSwap(false)}
          onSuccess={() => onSuccess()}
        />
      ) : showTransfer ? (
        <TransferBedPanel
          tenant={{ ...tenant, roomNumber, bedLabel }}
          hostelId={hostelId}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => onSuccess()}
        />
      ) : (
        <CheckoutForm
          onSubmit={d => checkoutMutation.mutate(d as Record<string, unknown>)}
          isPending={checkoutMutation.isPending}
          onCancel={() => setShowCheckout(false)}
        />
      )}
      {checkoutMutation.isError && (
        <p className="text-red-600 text-sm mt-2">{(checkoutMutation.error as Error).message}</p>
      )}
    </div>
  );
}

// ── Reserved bed view ──────────────────────────────────────
function ReservedBedView({ bedId, onSuccess }: { bedId: string; onSuccess: (goToRent?: boolean) => void }) {
  const { t } = useTranslation();
  const [showCheckin, setShowCheckin] = useState(false);
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: tenant, isLoading } = useQuery<Tenant | null>({
    queryKey: ['bed-tenant', bedId],
    queryFn: () => client.get(`/api/beds/${bedId}/tenant`).then(r => r as unknown as Tenant | null),
  });

  const checkinMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      client.post(`/api/tenants/${tenant!.id}/checkin`, body),
    onSuccess: () => onSuccess(true),
  });

  const cancelMutation = useMutation({
    mutationFn: () => client.delete(`/api/tenants/${tenant!.id}/reservation`),
    onSuccess: () => onSuccess(),
  });

  if (isLoading) return <p className="text-gray-400 py-6 text-center">{t('common.loading')}</p>;
  if (!tenant) return <p className="text-gray-500 py-6 text-center">{t('supervisor.noReservationForBed', 'No reservation found for this bed.')}</p>;

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 overflow-hidden">
        <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-2">{t('supervisor.reservedTenant', 'Reserved Tenant')}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('common.name')}</p><p className="font-semibold text-gray-900 break-words">{tenant.fullName}</p></div>
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('common.phone')}</p><p className="font-semibold break-all">{tenant.phone}</p></div>
          {tenant.email && <div className="col-span-2 min-w-0"><p className="text-xs text-gray-400">{t('common.email')}</p><p className="font-medium break-all">{tenant.email}</p></div>}
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('supervisor.expectedJoin', 'Expected Join')}</p><p className="font-medium">{tenant.joiningDate}</p></div>
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('tenant.monthlyRent')}</p><p className="font-semibold text-violet-700">{tenant.monthlyRent != null ? `₹${Number(tenant.monthlyRent).toLocaleString()}/mo` : '—'}</p></div>
          {tenant.notes && <div className="col-span-2 min-w-0"><p className="text-xs text-gray-400">{t('supervisor.notes', 'Notes')}</p><p className="text-sm break-words">{tenant.notes}</p></div>}
        </div>
      </div>

      {!showCheckin ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowCheckin(true)}
            className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ✓ {t('supervisor.checkInTenant', 'Check In Tenant')}
          </button>
          <button onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}
            className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {cancelMutation.isPending ? t('supervisor.cancelling', 'Cancelling…') : `✕ ${t('supervisor.cancelReservation', 'Cancel Reservation')}`}
          </button>
        </div>
      ) : (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
          <p className="font-semibold text-violet-900 mb-3">{t('supervisor.confirmCheckin', 'Confirm Check-In')}</p>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.actualJoiningDateRequired', 'Actual Joining Date *')}</label>
            <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm w-48" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => checkinMutation.mutate({ joiningDate })} disabled={checkinMutation.isPending}
              className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
              {checkinMutation.isPending ? t('supervisor.checkingIn', 'Checking In…') : t('supervisor.confirmCheckin', 'Confirm Check-In')}
            </button>
            <button onClick={() => setShowCheckin(false)} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.back')}</button>
          </div>
          {checkinMutation.isError && <p className="text-red-600 text-sm mt-2">{(checkinMutation.error as Error).message}</p>}
        </div>
      )}
      {cancelMutation.isError && <p className="text-red-600 text-sm mt-2">{(cancelMutation.error as Error).message}</p>}
    </div>
  );
}

// ── Admit Tenant form ──────────────────────────────────────
function AdmitTenantForm({ hostelId, bedId, defaultRent, hostel, onSuccess, onCancel }: {
  hostelId: string; bedId: string; defaultRent: number; hostel: Hostel | null;
  onSuccess: (goToRent?: boolean) => void; onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'admit' | 'reserve'>('admit');
  const [onBehalf, setOnBehalf] = useState<SignOnBehalfValue>(EMPTY_SIGN_ON_BEHALF);
  const [form, setForm] = useState({
    fullName: '', phone: '', email: '', gender: '', dateOfBirth: '',
    occupation: '', companyOrInstitution: '',
    permanentAddress: '', city: '', state: '', pincode: '',
    govIdType: '',
    joiningDate: new Date().toISOString().split('T')[0],
    expectedCheckoutDate: '',
    monthlyRent: String(defaultRent), securityDeposit: '',
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRel: '',
    notes: '',
    fatherName: '', fatherContact: '', fatherOccupation: '',
    motherName: '', motherContact: '', motherOccupation: '',
  });
  const [photos, setPhotos] = useState({ photoUrl: '' });
  const [govIds, setGovIds] = useState([{ govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }]);
  const [reactivateTenantId, setReactivateTenantId] = useState<string | null>(null);
  const phoneLookup = useTenantPhoneLookup();
  const PHONE_KEYS = new Set(['phone', 'emergencyContactPhone', 'fatherContact', 'motherContact']);
  const f = (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm(p => ({ ...p, [key]: PHONE_KEYS.has(key as string) ? sanitizePhoneInput(e.target.value) : e.target.value }));
      if (key === 'phone') setReactivateTenantId(null);
    };

  async function handlePhoneBlur() {
    await phoneLookup.lookup(form.phone);
  }

  function handlePrefillBasic(fullName: string, email: string) {
    setForm(p => ({ ...p, fullName: p.fullName || fullName, email: p.email || email }));
    phoneLookup.reset();
  }

  function handleFetchDetails(tenant: Tenant) {
    setForm(p => ({
      ...p,
      fullName: tenant.fullName, phone: tenant.phone, email: tenant.email ?? '',
      gender: tenant.gender ?? '', dateOfBirth: tenant.dateOfBirth ?? '',
      occupation: tenant.occupation ?? '', companyOrInstitution: tenant.companyOrInstitution ?? '',
      permanentAddress: tenant.permanentAddress ?? '', city: tenant.city ?? '', state: tenant.state ?? '', pincode: tenant.pincode ?? '',
      govIdType: tenant.govIdType ?? '',
      monthlyRent: tenant.monthlyRent != null ? String(tenant.monthlyRent) : p.monthlyRent,
      securityDeposit: tenant.securityDeposit != null ? String(tenant.securityDeposit) : p.securityDeposit,
      emergencyContactName: tenant.emergencyContactName ?? '', emergencyContactPhone: tenant.emergencyContactPhone ?? '',
      emergencyContactRel: tenant.emergencyContactRel ?? '',
      fatherName: tenant.fatherName ?? '', fatherContact: tenant.fatherContact ?? '', fatherOccupation: tenant.fatherOccupation ?? '',
      motherName: tenant.motherName ?? '', motherContact: tenant.motherContact ?? '', motherOccupation: tenant.motherOccupation ?? '',
    }));
    setPhotos({ photoUrl: tenant.photoUrl ?? '' });
    setReactivateTenantId(tenant.id);
    phoneLookup.reset();
  }

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => client.post('/api/tenants', body),
    onSuccess: (_data, variables) => {
      const isReserve = (variables as Record<string, unknown>).status === 'RESERVED';
      onSuccess(!isReserve);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      hostelId, bedId,
      fullName: form.fullName, phone: form.phone,
      email: form.email || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      gender: form.gender || undefined,
      govIdType: form.govIdType || undefined,
      joiningDate: form.joiningDate,
      expectedCheckoutDate: form.expectedCheckoutDate || undefined,
      monthlyRent: Number(form.monthlyRent),
      securityDeposit: form.securityDeposit ? Number(form.securityDeposit) : 0,
      emergencyContactName: form.emergencyContactName || undefined,
      emergencyContactPhone: form.emergencyContactPhone || undefined,
      occupation: form.occupation || undefined,
      companyOrInstitution: form.companyOrInstitution || undefined,
      permanentAddress: form.permanentAddress || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      pincode: form.pincode || undefined,
      emergencyContactRelation: form.emergencyContactRel || undefined,
      notes: form.notes || undefined,
      photoUrl: photos.photoUrl || undefined,
      govIds: govIds.filter(g => g.frontPhotoUrl || g.backPhotoUrl || g.govIdType).map(g => ({
        govIdType: g.govIdType || undefined,
        frontPhotoUrl: g.frontPhotoUrl || undefined,
        backPhotoUrl: g.backPhotoUrl || undefined,
      })),
      fatherName: form.fatherName || undefined,
      fatherContact: form.fatherContact || undefined,
      fatherOccupation: form.fatherOccupation || undefined,
      motherName: form.motherName || undefined,
      motherContact: form.motherContact || undefined,
      motherOccupation: form.motherOccupation || undefined,
      status: mode === 'reserve' ? 'RESERVED' : undefined,
      termsAccepted: (onBehalf.enabled && onBehalf.agreed && !!onBehalf.signature) || undefined,
      termsSignature: onBehalf.signature || undefined,
      reactivateTenantId: reactivateTenantId || undefined,
    });
  };

  const onBehalfIncomplete = onBehalf.enabled && (!onBehalf.agreed || !onBehalf.signature);
  const inp = 'w-full border rounded-lg px-3 py-2 text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {phoneLookup.result && (
        <TenantPhoneMatchDialog
          result={phoneLookup.result}
          role="supervisor"
          currentHostelId={hostelId}
          onFetchDetails={handleFetchDetails}
          onPrefillBasic={handlePrefillBasic}
          onStartFresh={() => phoneLookup.reset()}
          onClose={() => phoneLookup.reset()}
        />
      )}
      {/* Mode toggle */}
      <div className="flex rounded-lg border border-gray-200 overflow-hidden">
        <button type="button" onClick={() => setMode('admit')}
          className={`flex-1 py-2 text-sm font-medium transition ${mode === 'admit' ? 'bg-violet-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
          {t('supervisor.admitNow', 'Admit Now')}
        </button>
        <button type="button" onClick={() => setMode('reserve')}
          className={`flex-1 py-2 text-sm font-medium transition ${mode === 'reserve' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
          {t('supervisor.reserveBed', 'Reserve Bed')}
        </button>
      </div>
      {mode === 'reserve' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('supervisor.bedWillBeMarkedReserved', 'Bed will be marked')} <strong>{t('common.reserved')}</strong>. {t('supervisor.tenantCanCheckInLater', 'The tenant can check in later to activate the bed.')}
        </p>
      )}

      {/* Profile Photo */}
      <div className="flex items-start gap-4 pb-4 border-b border-gray-100">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">{t('supervisor.profilePhoto', 'Profile Photo')}</p>
          <PhotoCapture value={photos.photoUrl} onChange={v => setPhotos(p => ({ ...p, photoUrl: v }))} mode="square" />
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fullNameRequired', 'Full Name *')}</label>
            <input required value={form.fullName} onChange={f('fullName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.phoneRequired', 'Phone *')}</label>
            <input type="tel" inputMode="tel" required value={form.phone} onChange={f('phone')} onBlur={() => void handlePhoneBlur()} className={inp} />
            {phoneLookup.loading ? (
              <p className="text-[11px] text-gray-400 mt-1">{t('tenant.checkingPhone', 'Checking…')}</p>
            ) : reactivateTenantId ? (
              <p className="text-[11px] text-teal-600 mt-1">{t('tenant.reactivatingPreviousStay', 'Reactivating this tenant’s previous profile.')}</p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1">{t('tenant.phoneSearchHint', 'Enter phone number to search for an existing profile.')}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.gender', 'Gender')}</label>
            <Select value={form.gender} onChange={f('gender')} options={[
              { value: '', label: t('supervisor.select', 'Select') },
              { value: 'MALE', label: t('supervisor.male', 'Male') },
              { value: 'FEMALE', label: t('supervisor.female', 'Female') },
              { value: 'OTHER', label: t('supervisor.other', 'Other') },
            ]} />
          </div>
        </div>
      </div>

      {/* Personal */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.email')}</label>
          <input type="email" value={form.email} onChange={f('email')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.dateOfBirth', 'Date of Birth')}</label>
          <input type="date" value={form.dateOfBirth} onChange={f('dateOfBirth')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.occupation', 'Occupation')}</label>
          <input value={form.occupation} onChange={f('occupation')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.companyOrInstitution', 'Company / Institution')}</label>
          <input value={form.companyOrInstitution} onChange={f('companyOrInstitution')} className={inp} />
        </div>
      </div>

      {/* Address */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('common.address')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.permanentAddress', 'Permanent Address')}</label>
            <textarea value={form.permanentAddress} onChange={f('permanentAddress')} rows={2} className={`${inp} resize-none`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.city', 'City')}</label>
            <input value={form.city} onChange={f('city')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.state', 'State')}</label>
            <input value={form.state} onChange={f('state')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.pincode', 'Pincode')}</label>
            <input value={form.pincode} onChange={f('pincode')} className={inp} />
          </div>
        </div>
      </div>

      {/* ID & Financials */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.idAndFinancials', 'ID & Financials')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.govIdType', 'Gov ID Type')}</label>
            <Select value={form.govIdType} onChange={f('govIdType')} options={[
              { value: '', label: '—' },
              ...['AADHAAR','PASSPORT','DRIVING_LICENSE','VOTER_ID','NATIONAL_ID','OTHER'].map(idType => ({ value: idType, label: idType })),
            ]} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {mode === 'reserve' ? t('supervisor.expectedJoiningDateRequired', 'Expected Joining Date *') : t('supervisor.joiningDateRequired', 'Joining Date *')}
            </label>
            <input type="date" required value={form.joiningDate} onChange={f('joiningDate')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.monthlyRentRupeesRequired', 'Monthly Rent (₹) *')}</label>
            <input required type="number" value={form.monthlyRent} onChange={f('monthlyRent')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.securityDepositRupees', 'Security Deposit (₹)')}</label>
            <input type="number" value={form.securityDeposit} onChange={f('securityDeposit')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.expectedCheckout', 'Expected Checkout')}</label>
            <input type="date" value={form.expectedCheckoutDate} onChange={f('expectedCheckoutDate')} className={inp} />
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.emergencyContact', 'Emergency Contact')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.name')}</label>
            <input value={form.emergencyContactName} onChange={f('emergencyContactName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')}</label>
            <input type="tel" inputMode="tel" value={form.emergencyContactPhone} onChange={f('emergencyContactPhone')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.relation', 'Relation')}</label>
            <input value={form.emergencyContactRel} onChange={f('emergencyContactRel')} placeholder={t('supervisor.relationPlaceholder', 'e.g. Father, Spouse')} className={inp} />
          </div>
        </div>
      </div>

      {/* Government IDs */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.governmentIds', 'Government IDs')}</p>
        <div className="space-y-4">
          {govIds.map((g, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3 relative bg-gray-50/40">
              {govIds.length > 1 && (
                <button type="button" onClick={() => setGovIds(prev => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-3 right-3 text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
              <Select value={g.govIdType} onChange={e => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, govIdType: e.target.value } : x))}
                className="mr-6" options={[
                  { value: '', label: t('supervisor.idTypePlaceholder', '— ID Type —') },
                  ...['AADHAAR','PASSPORT','DRIVING_LICENSE','VOTER_ID','NATIONAL_ID','OTHER'].map(idType => ({ value: idType, label: idType })),
                ]} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">{t('supervisor.frontSide', 'Front Side')}</p>
                  <PhotoCapture value={g.frontPhotoUrl} onChange={v => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, frontPhotoUrl: v } : x))} mode="landscape" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">{t('supervisor.backSide', 'Back Side')}</p>
                  <PhotoCapture value={g.backPhotoUrl} onChange={v => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, backPhotoUrl: v } : x))} mode="landscape" />
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setGovIds(prev => [...prev, { govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }])}
            className="flex items-center gap-1.5 text-sm text-violet-700 border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50">
            ＋ {t('supervisor.addAnotherId', 'Add Another ID')}
          </button>
        </div>
      </div>

      {/* Parent / Guardian */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('supervisor.parentGuardianDetails', 'Parent / Guardian Details')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 text-xs font-medium text-gray-500 -mb-1">{t('supervisor.fathersDetails', "Father's Details")}</div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersName', "Father's Name")}</label><input value={form.fatherName} onChange={f('fatherName')} className={inp} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersContact', "Father's Contact")}</label><input type="tel" inputMode="tel" value={form.fatherContact} onChange={f('fatherContact')} className={inp} /></div>
          <div className="col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.fathersOccupation', "Father's Occupation")}</label><input value={form.fatherOccupation} onChange={f('fatherOccupation')} className={inp} /></div>
          <div className="col-span-2 text-xs font-medium text-gray-500 mt-1 -mb-1">{t('supervisor.mothersDetails', "Mother's Details")}</div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersName', "Mother's Name")}</label><input value={form.motherName} onChange={f('motherName')} className={inp} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersContact', "Mother's Contact")}</label><input type="tel" inputMode="tel" value={form.motherContact} onChange={f('motherContact')} className={inp} /></div>
          <div className="col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.mothersOccupation', "Mother's Occupation")}</label><input value={form.motherOccupation} onChange={f('motherOccupation')} className={inp} /></div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.notes', 'Notes')}</label>
        <textarea value={form.notes} onChange={f('notes')} rows={2} className={`${inp} resize-none`} />
      </div>

      {/* Sign on behalf (no smartphone) */}
      <div className="border-t border-gray-100 pt-4">
        <SignOnBehalfFields hostel={hostel} value={onBehalf} onChange={setOnBehalf} />
      </div>

      {mutation.isError && <p className="text-red-600 text-sm">{(mutation.error as Error).message}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={mutation.isPending || onBehalfIncomplete}
          className={`px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 text-white ${mode === 'reserve' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-violet-600 hover:bg-violet-700'}`}>
          {mutation.isPending
            ? (mode === 'reserve' ? t('supervisor.reserving', 'Reserving…') : t('supervisor.admitting', 'Admitting…'))
            : (mode === 'reserve' ? t('supervisor.reserveBed', 'Reserve Bed') : t('supervisor.admitTenant', 'Admit Tenant'))}
        </button>
        <button type="button" onClick={onCancel} className="border px-5 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
    </form>
  );
}

// ── Bed modal ──────────────────────────────────────────────
function BedModal({ bed, room, hostelId, hostel, onClose }: {
  bed: Bed; room: Room; hostelId: string; hostel: Hostel | null; onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const handleSuccess = (goToRent?: boolean) => {
    void qc.invalidateQueries({ queryKey: ['beds', room.id] });
    void qc.invalidateQueries({ queryKey: ['bed-tenant', bed.id] });
    void qc.invalidateQueries({ queryKey: ['sup-hostel-stats', hostelId] });
    onClose();
    if (goToRent) {
      void navigate({ to: '/supervisor/rent', search: { hostelId: '', status: '', from: '', to: '' } });
    }
  };

  const deleteBedMutation = useMutation({
    mutationFn: () => client.delete(`/api/beds/${bed.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      onClose();
    },
  });

  const handleDeleteBed = () => {
    if (window.confirm(t('owner.confirmDeleteBed', 'Delete this bed? This cannot be undone.'))) {
      deleteBedMutation.mutate();
    }
  };

  const statusLabel: Record<string, string> = {
    VACANT: t('supervisor.admitTenant', 'Admit Tenant'),
    RESERVED: t('supervisor.reservedBedStatus', 'Reserved Bed'),
    OCCUPIED: t('supervisor.tenantProfile', 'Tenant Profile'),
    MAINTENANCE: t('supervisor.bedMaintenance', 'Bed Maintenance'),
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{statusLabel[bed.status] ?? bed.status}</h2>
            <p className="text-sm text-gray-500">{t('supervisor.roomBedLabel', 'Room {{room}} · Bed {{bed}}', { room: room.roomNumber, bed: bed.bedLabel })}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          {bed.status === 'OCCUPIED' ? (
            <>
              <OccupiedBedView bedId={bed.id} hostelId={hostelId} roomNumber={room.roomNumber} bedLabel={bed.bedLabel} onSuccess={() => handleSuccess()} />
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                {t('owner.bedHasTenantCannotDelete', 'This bed has a tenant assigned. Move them to another bed (Swap Bed) or check them out, then delete the bed.')}
              </p>
            </>
          ) : bed.status === 'RESERVED' ? (
            <>
              <ReservedBedView bedId={bed.id} onSuccess={handleSuccess} />
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                {t('owner.bedHasTenantCannotDelete', 'This bed has a tenant assigned. Move them to another bed (Swap Bed) or check them out, then delete the bed.')}
              </p>
            </>
          ) : bed.status === 'MAINTENANCE' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BedDouble size={28} className="text-gray-400" />
              </div>
              <p className="font-medium text-gray-700 mb-1">{t('supervisor.bedUnderMaintenance', 'Bed {{bed}} is under maintenance', { bed: bed.bedLabel })}</p>
              <p className="text-sm text-gray-500">{t('supervisor.contactOwnerBedAvailable', 'Contact the hostel owner to make this bed available.')}</p>
            </div>
          ) : (
            <>
              <AdmitTenantForm hostelId={hostelId} bedId={bed.id} defaultRent={room.rentAmount} hostel={hostel}
                onSuccess={handleSuccess} onCancel={onClose} />
              <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                <button
                  onClick={handleDeleteBed}
                  disabled={deleteBedMutation.isPending}
                  className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 size={12} /> {t('owner.deleteThisBed', 'Delete this bed')}
                </button>
              </div>
            </>
          )}
          {deleteBedMutation.isError && (
            <p className="text-red-600 text-sm mt-2">{(deleteBedMutation.error as Error).message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Room card (no room-level add/edit/delete; beds can be added/removed) ──
function RoomCard({ room, onBedClick }: {
  room: Room; onBedClick: (bed: Bed) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: beds = [] } = useQuery<Bed[]>({
    queryKey: ['beds', room.id],
    queryFn: () => client.get(`/api/rooms/${room.id}/beds`).then(r => r as unknown as Bed[]),
    enabled: open,
  });

  const addBedMutation = useMutation({
    mutationFn: () => client.post(`/api/rooms/${room.id}/beds`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });

  const occupied = beds.filter(b => b.status === 'OCCUPIED').length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer transition"
        onClick={() => setOpen(o => !o)}>
        {open ? <ChevronDown size={15} className="text-gray-400 shrink-0" /> : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
        <BedDouble size={15} className="text-violet-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900">{t('supervisor.roomNumberLabel', 'Room {{number}}', { number: room.roomNumber })}</span>
          {room.roomName && <span className="text-gray-500 text-sm ml-1.5">— {room.roomName}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
          <span>{t('supervisor.occupiedOfTotal', '{{occupied}}/{{total}} occupied', { occupied, total: room.totalBeds })}</span>
          <span className="font-medium text-violet-700">₹{Number(room.rentAmount).toLocaleString()}/mo</span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${room.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {room.status}
          </span>
        </div>
      </div>
      {open && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            {beds.length === 0 && <span className="text-xs text-gray-400">{t('supervisor.noBedsFound', 'No beds found')}</span>}
            {beds.map(b => (
              <BedCard key={b.id} bed={b} onClick={() => { if (b.status !== 'MAINTENANCE') onBedClick(b); }} />
            ))}
            <button
              onClick={() => addBedMutation.mutate()}
              disabled={addBedMutation.isPending}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-dashed border-gray-300 text-gray-500 hover:border-violet-400 hover:text-violet-600 transition disabled:opacity-50"
            >
              <Plus size={12} /> {t('owner.addBed', 'Add Bed')}
            </button>
          </div>
          {addBedMutation.isError && <p className="text-red-600 text-xs mt-2">{(addBedMutation.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}

// ── Floor section (read-only: no add/edit/delete) ─────────
function FloorSection({ floor, onBedClick }: {
  floor: Floor; onBedClick: (bed: Bed, room: Room) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const { data: rooms = [] } = useQuery<Room[]>({
    queryKey: ['rooms', floor.id],
    queryFn: () => client.get(`/api/rooms?floorId=${floor.id}`).then(r => r as unknown as Room[]),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <button onClick={() => setOpen(o => !o)} className="text-gray-500 hover:text-gray-700">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <Layers size={16} className="text-blue-500 shrink-0" />
        <span className="font-semibold text-gray-800 flex-1">{floor.floorName}</span>
        <span className="text-xs text-gray-500">{t('supervisor.roomCount', '{{count}} room{{s}}', { count: rooms.length, s: rooms.length !== 1 ? 's' : '' })}</span>
      </div>
      {open && (
        <div className="p-4 space-y-2">
          {rooms.length === 0
            ? <p className="text-sm text-gray-400 py-2">{t('supervisor.noRoomsOnFloor', 'No rooms on this floor.')}</p>
            : rooms.map(r => (
              <RoomCard key={r.id} room={r} onBedClick={b => onBedClick(b, r)} />
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── Vacant Beds Panel ──────────────────────────────────────
function VacantBedsPanel({ hostelId, onClose, onBedClick }: {
  hostelId: string; onClose: () => void; onBedClick: (bed: Bed, room: Room) => void;
}) {
  const { t } = useTranslation();
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['rooms-all', hostelId],
    queryFn: () => client.get(`/api/rooms?hostelId=${hostelId}`).then(r => r as unknown as Room[]),
  });

  const bedResults = useQueries({
    queries: rooms.map(r => ({
      queryKey: ['beds', r.id],
      queryFn: () => client.get(`/api/rooms/${r.id}/beds`).then(res => res as unknown as Bed[]),
    })),
  });

  const isLoading = roomsLoading || bedResults.some(r => r.isLoading);

  const vacantGroups = rooms
    .map((room, i) => ({
      room,
      vacantBeds: (bedResults[i]?.data ?? []).filter(b => b.status === 'VACANT'),
    }))
    .filter(g => g.vacantBeds.length > 0);

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-green-900 flex items-center gap-2">
          <BedDouble size={16} className="text-green-600" />
          {t('supervisor.vacantBeds', 'Vacant Beds')}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400">{t('supervisor.loadingVacantBeds', 'Loading vacant beds…')}</p>
      ) : vacantGroups.length === 0 ? (
        <p className="text-sm text-gray-500">{t('supervisor.noVacantBedsAtHostel', 'No vacant beds at this hostel.')}</p>
      ) : (
        <div className="space-y-2">
          {vacantGroups.map(({ room, vacantBeds }) => (
            <div key={room.id} className="bg-white rounded-lg p-3 border border-green-100">
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-medium text-gray-800">
                  {t('supervisor.roomNumberLabel', 'Room {{number}}', { number: room.roomNumber })}
                  {room.roomName && <span className="text-gray-500 font-normal ml-1.5">— {room.roomName}</span>}
                  {room.floorNumber != null && (
                    <span className="text-xs text-gray-400 ml-2">{t('supervisor.floorNumberLabel', 'Floor {{number}}', { number: room.floorNumber })}</span>
                  )}
                </p>
                <span className="text-xs font-semibold text-violet-700">₹{Number(room.rentAmount).toLocaleString()}/mo</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {vacantBeds.map(b => (
                  <button key={b.id}
                    onClick={() => { onClose(); onBedClick(b, room); }}
                    className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold hover:bg-green-200 hover:ring-1 hover:ring-green-400 transition">
                    {t('supervisor.bedAdmitLabel', 'Bed {{label}} · Admit', { label: b.bedLabel })}
                  </button>
                ))}
                <span className="text-xs text-gray-500 self-center ml-1">
                  {t('supervisor.bedsFreeOfTotal', '{{free}} of {{total}} bed{{s}} free', { free: vacantBeds.length, total: room.totalBeds, s: Number(room.totalBeds) !== 1 ? 's' : '' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Maintenance Beds Panel ─────────────────────────────────
function MaintenanceBedsPanel({ hostelId, onClose, onBedClick }: {
  hostelId: string; onClose: () => void; onBedClick: (bed: Bed, room: Room) => void;
}) {
  const { t } = useTranslation();
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['rooms-all', hostelId],
    queryFn: () => client.get(`/api/rooms?hostelId=${hostelId}`).then(r => r as unknown as Room[]),
  });

  const bedResults = useQueries({
    queries: rooms.map(r => ({
      queryKey: ['beds', r.id],
      queryFn: () => client.get(`/api/rooms/${r.id}/beds`).then(res => res as unknown as Bed[]),
    })),
  });

  const isLoading = roomsLoading || bedResults.some(r => r.isLoading);

  const maintenanceGroups = rooms
    .map((room, i) => ({
      room,
      maintenanceBeds: (bedResults[i]?.data ?? []).filter(b => b.status === 'MAINTENANCE'),
    }))
    .filter(g => g.maintenanceBeds.length > 0);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Wrench size={16} className="text-gray-500" />
          {t('supervisor.maintenanceBeds', 'Maintenance Beds')}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400">{t('supervisor.loadingMaintenanceBeds', 'Loading maintenance beds…')}</p>
      ) : maintenanceGroups.length === 0 ? (
        <p className="text-sm text-gray-500">{t('supervisor.noMaintenanceBedsAtHostel', 'No beds under maintenance at this hostel.')}</p>
      ) : (
        <div className="space-y-2">
          {maintenanceGroups.map(({ room, maintenanceBeds }) => (
            <div key={room.id} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-medium text-gray-800">
                  {t('supervisor.roomNumberLabel', 'Room {{number}}', { number: room.roomNumber })}
                  {room.roomName && <span className="text-gray-500 font-normal ml-1.5">— {room.roomName}</span>}
                  {room.floorNumber != null && (
                    <span className="text-xs text-gray-400 ml-2">{t('supervisor.floorNumberLabel', 'Floor {{number}}', { number: room.floorNumber })}</span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {maintenanceBeds.map(b => (
                  <button key={b.id}
                    onClick={() => { onClose(); onBedClick(b, room); }}
                    className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded text-xs font-semibold hover:bg-gray-300 hover:ring-1 hover:ring-gray-400 transition">
                    {t('supervisor.bedManageLabel', 'Bed {{label}} · Manage', { label: b.bedLabel })}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export function SupervisorHostelDetailPage() {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ from: '/protected/supervisor/hostels/$id' });
  const { bedStatus } = useSearch({ from: '/protected/supervisor/hostels/$id' });
  const navigate = useNavigate();
  const [showVacant, setShowVacant] = useState(bedStatus === 'VACANT');
  const [showMaintenance, setShowMaintenance] = useState(bedStatus === 'MAINTENANCE');
  const [bedModal, setBedModal] = useState<{ bed: Bed; room: Room } | null>(null);

  const { data: hostel } = useQuery<Hostel>({
    queryKey: ['hostel', hostelId],
    queryFn: () => client.get(`/api/hostels/${hostelId}`).then(r => r as unknown as Hostel),
  });

  const { data: floors = [], isLoading } = useQuery<Floor[]>({
    queryKey: ['floors', hostelId],
    queryFn: () => client.get(`/api/floors?hostelId=${hostelId}`).then(r => r as unknown as Floor[]),
  });

  const { data: stats } = useQuery<HostelStats>({
    queryKey: ['sup-hostel-stats', hostelId],
    queryFn: () => client.get(`/api/dashboard/supervisor/${hostelId}`).then(r => r as unknown as HostelStats),
  });

  const s = stats ?? { activeTenants: 0, reservedBeds: 0, vacantBeds: 0, checkedOutTenants: 0, cancelledTenants: 0, blockedBeds: 0 };

  function handleBedClick(bed: Bed, room: Room) {
    if (bed.status === 'OCCUPIED') {
      void client.get(`/api/beds/${bed.id}/tenant`).then((tenant: unknown) => {
        const t = tenant as Tenant | null;
        if (t) {
          void navigate({ to: '/supervisor/hostels/$id/tenants/$tenantId', params: { id: hostelId, tenantId: t.id }, search: { tab: 'profile' } });
        } else {
          setBedModal({ bed, room });
        }
      });
      return;
    }
    setBedModal({ bed, room });
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-1">
        <Link to="/supervisor/dashboard" className="text-violet-600 text-sm hover:underline">← {t('nav.hostels')}</Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-500">{hostel?.name ?? '…'}</span>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 size={22} className="text-violet-600" />
          {hostel?.name ?? t('supervisor.hostel', 'Hostel')}
        </h1>
        {hostel && <p className="text-sm text-gray-500 mt-0.5">{hostel.city}, {hostel.state}</p>}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-6 gap-1.5 mb-4">
        <button
          onClick={() => void navigate({ to: '/supervisor/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'ACTIVE' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-violet-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center mb-1 group-hover:bg-violet-100 transition">
            <Users size={12} className="text-violet-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.activeTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.active')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/supervisor/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'RESERVED' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-amber-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center mb-1 group-hover:bg-amber-100 transition">
            <BedDouble size={12} className="text-amber-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.reservedBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.reserved')}</p>
        </button>
        <button
          onClick={() => setShowVacant(v => !v)}
          className={`bg-white rounded-lg p-2 shadow-sm border flex flex-col items-center text-center hover:border-green-300 hover:shadow-md transition group ${showVacant ? 'border-green-400 ring-1 ring-green-300' : 'border-gray-100'}`}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center mb-1 transition ${showVacant ? 'bg-green-100' : 'bg-green-50 group-hover:bg-green-100'}`}>
            <BedDouble size={12} className="text-green-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.vacantBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('supervisor.vacant', 'Vacant')}</p>
        </button>
        <button
          onClick={() => setShowMaintenance(v => !v)}
          className={`bg-white rounded-lg p-2 shadow-sm border flex flex-col items-center text-center hover:border-gray-300 hover:shadow-md transition group ${showMaintenance ? 'border-gray-400 ring-1 ring-gray-300' : 'border-gray-100'}`}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center mb-1 transition ${showMaintenance ? 'bg-gray-200' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
            <Wrench size={12} className="text-gray-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.blockedBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('supervisor.maintenance', 'Maint.')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/supervisor/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'CHECKED_OUT' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-gray-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center mb-1 group-hover:bg-gray-200 transition">
            <LogOut size={12} className="text-gray-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.checkedOutTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.checkedOut')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/supervisor/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'CANCELLED' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-red-200 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center mb-1 group-hover:bg-red-100 transition">
            <Ban size={12} className="text-red-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.cancelledTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.cancelled')}</p>
        </button>
      </div>

      {/* Vacant Beds Panel */}
      {showVacant && (
        <VacantBedsPanel
          hostelId={hostelId}
          onClose={() => setShowVacant(false)}
          onBedClick={(bed, room) => setBedModal({ bed, room })}
        />
      )}

      {/* Maintenance Beds Panel */}
      {showMaintenance && (
        <MaintenanceBedsPanel
          hostelId={hostelId}
          onClose={() => setShowMaintenance(false)}
          onBedClick={(bed, room) => setBedModal({ bed, room })}
        />
      )}

      {/* Floors section */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Layers size={18} className="text-blue-500" />
          {t('supervisor.floorsAndRooms', 'Floors & Rooms')}
          <span className="text-sm font-normal text-gray-400">({t('supervisor.floorCount', '{{count}} floor{{s}}', { count: floors.length, s: floors.length !== 1 ? 's' : '' })})</span>
        </h2>
        <div className="flex gap-2">
          <Link to="/supervisor/hostels/$id/complaints" params={{ id: hostelId }}
            className="flex items-center gap-1.5 text-sm text-red-600 font-medium border border-red-200 bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100">
            <AlertCircle size={14} /> {t('nav.complaints')}
          </Link>
          <Link to="/supervisor/hostels/$id/tenants" params={{ id: hostelId }} search={{ status: 'ACTIVE' }}
            className="flex items-center gap-1.5 text-sm text-violet-600 font-medium border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50">
            <Users size={14} /> {t('nav.tenants')}
          </Link>
          <Link to="/supervisor/rent" search={{ hostelId: '', status: '', from: '', to: '' }}
            className="flex items-center gap-1.5 text-sm text-violet-600 font-medium border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50">
            <CreditCard size={14} /> {t('nav.rent')}
          </Link>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-400 py-8 text-center">{t('supervisor.loadingFloors', 'Loading floors…')}</p>
      ) : floors.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-10 text-center">
          <Layers size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-gray-600">{t('supervisor.noFloorsYet', 'No floors yet')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('supervisor.contactOwnerFloorsRooms', 'Contact the owner to set up floors and rooms.')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {floors.map(f => (
            <FloorSection key={f.id} floor={f} onBedClick={handleBedClick} />
          ))}
        </div>
      )}

      {bedModal && (
        <BedModal
          bed={bedModal.bed}
          room={bedModal.room}
          hostelId={hostelId}
          hostel={hostel ?? null}
          onClose={() => setBedModal(null)}
        />
      )}
    </div>
  );
}
